import * as React from "react";
import { clsx } from "@plans/appkit";
import { XCloseIcon } from "@plans/icons/untitled-ui/XCloseIcon";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useIsomorphicLayoutEffect } from "@react-spring/web";
import { useResizeObserver } from "use-resize-observer";
import { useWindowSize } from "~/lib/ui/useWindowSize";
import { Button } from "./Button";

export type DialogPlacement = "center" | "right";

export function dialogContentPlacementClassName(
	placement: DialogPlacement = "center",
): string {
	return placement === "right"
		? "animate animate-dialogSheetShow h-svh max-h-none max-w-none self-stretch justify-self-end rounded-l-lg"
		: "animate animate-dialogContentShow scale-125 opacity-0 max-w-[calc(100vw-8vmin)] max-h-[calc(100svh-8vmin)] rounded-lg";
}

type DialogContentProps = {
	children: React.ReactNode;
	className?: string;
	/**
	 * Title to render at the top of the header of the dialog.
	 */
	title?: React.ReactNode;
	/**
	 * Extra classes on the title element, e.g. `text-center`. The close button is
	 * absolutely positioned, so centering here needs no padding compensation.
	 */
	titleClassName?: string;
	/**
	 * Content to put below the title in the header of the dialog.
	 */
	subtitle?: React.ReactNode;
	/**
	 * Content to put in the footer slot of the dialog.
	 */
	footerContent?: React.ReactNode;
	testId?: string;
	isCloseButtonHidden?: boolean;
	isCloseOnInteractOutsideDisabled?: boolean;
	isCloseOnEscapeDisabled?: boolean;
	/**
	 * Whether or not the scroll area is inset from the edges of the dialog.
	 *
	 * This is useful when the scroll area has some content that must align with
	 * the edges of the dialog. Defaults to true. If set to false, the consumer of
	 * the component must ensure that the scroll area has padding to ensure that
	 * the content aligns with the header and footer.
	 */
	isScrollAreaInset?: boolean;
	isFooterFloating?: boolean;
	footerClassName?: string;
	/** Content that covers the dialog panel without opening a second dialog. */
	overlayContent?: React.ReactNode;
	/**
	 * Render a bottom border on the dialog `<header>` (outside the scroll
	 * container) to visually separate the title from a `bg-background-soft`
	 * scrollable body. Pairs naturally with `isScrollAreaInset={false}`.
	 */
	hasHeaderBorder?: boolean;
	/** Position the panel as a centered modal or a full-height right sheet. */
	placement?: DialogPlacement;
	ref?: React.Ref<HTMLDivElement | null>;
};

/**
 * Extension of the primitive DialogContent component with some core styling for the overlay
 * and content. Including a title and close button.
 * @see https://www.radix-ui.com/docs/primitives/components/dialog
 *
 * Useful as a base for more complex dialogs.
 */
export function DialogContent({
	children,
	title,
	titleClassName,
	className,
	testId,
	isCloseButtonHidden,
	isCloseOnInteractOutsideDisabled,
	isCloseOnEscapeDisabled,
	subtitle,
	footerContent,
	isScrollAreaInset = true,
	isFooterFloating,
	footerClassName,
	overlayContent,
	hasHeaderBorder,
	placement = "center",
	ref: forwardedRef,
}: DialogContentProps) {
	const actualRef = React.useRef<HTMLDivElement>(null);
	const scrollContainerRef = React.useRef<HTMLDivElement>(null);
	const scrollContainerContentRef = React.useRef<HTMLDivElement>(null);
	const [isScrolledToTop, setIsScrolledToTop] = React.useState(true);
	const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(true);
	const { height: windowHeight } = useWindowSize();
	const { height: scrollContainerContentHeight } = useResizeObserver({
		ref: scrollContainerContentRef,
	});

	const updateIsScrolledFlags = (scrollContainer: HTMLElement) => {
		const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
		setIsScrolledToTop(scrollTop <= 0);
		// Subtract 1 to account for rounding errors on uneven heights
		setIsScrolledToBottom(scrollTop + clientHeight >= scrollHeight - 1);
	};

	const onScrollContainerScroll = (
		event: React.UIEvent<HTMLDivElement, UIEvent>,
	) => {
		const scrollContainer = event.target as HTMLElement | null;
		if (!scrollContainer) {
			return;
		}
		// The vertical screen real estate of the modal is very limited when the
		// virtual keyboard is visible, so blur any focused inputs inside the
		// dialog when the user scrolls, which will cause the virtual keyboard
		// to be dismissed. A textarea raises the same keyboard as an input, so
		// both have to be caught here — `ManagedDialog` renders either one
		// depending on whether the prompt asked for prose.
		if (
			(document.activeElement instanceof HTMLInputElement ||
				document.activeElement instanceof HTMLTextAreaElement) &&
			actualRef.current?.contains(document.activeElement)
		) {
			document.activeElement.blur();
		}

		updateIsScrolledFlags(scrollContainer);
	};

	useIsomorphicLayoutEffect(() => {
		if (!scrollContainerRef.current) {
			return;
		}
		updateIsScrolledFlags(scrollContainerRef.current);
	}, [windowHeight, scrollContainerContentHeight]);

	// Assign any access to the forwarded ref to the internal one.
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	React.useImperativeHandle(forwardedRef, () => actualRef.current!, []);

	return (
		<DialogPrimitive.Portal>
			<DialogPrimitive.Overlay className="animate-dialogOverlayShow z-(--z-dialog) fixed inset-0 bg-black/50" />
			<DialogPrimitive.Content
				className="global-focus z-(--z-dialog) fixed"
				ref={actualRef}
				onInteractOutside={(event) => {
					if (isCloseOnInteractOutsideDisabled) {
						event.preventDefault();
						event.stopPropagation();
					}
				}}
				onEscapeKeyDown={(event) => {
					if (isCloseOnEscapeDisabled) {
						event.preventDefault();
					}
					// Stop propagation so parent handlers (e.g. editor back navigation)
					// don't also react to Escape after the dialog closes.
					event.stopPropagation();
				}}
			>
				<div
					data-cy={testId}
					className="z-(--z-dialog) pointer-events-none fixed inset-0 grid h-full w-full place-items-center"
				>
					<div
						style={{ animationFillMode: "forwards", gridArea: "1/1" }}
						className={clsx(
							"pointer-events-auto relative flex flex-col",
							"overflow-hidden bg-white text-left shadow-xl",
							dialogContentPlacementClassName(placement),
							isScrollAreaInset && "px-4 pb-4 sm:px-6 sm:pb-6",
							className,
						)}
					>
						{!isCloseButtonHidden && (
							<DialogPrimitive.Close asChild>
								<Button
									className="text-text-secondary absolute right-4 top-5 z-10 sm:right-5 sm:top-6"
									aria-label="Close"
									variant="text"
									size="small"
									isIconOnly
								>
									<XCloseIcon className="h-5 w-5 [&_path]:stroke-[2.25]" />
								</Button>
							</DialogPrimitive.Close>
						)}
						{(title || subtitle) && (
							<header
								className={clsx(
									"flex flex-col gap-3 pb-3 pt-5 sm:pb-4 sm:pt-6",
									!isScrollAreaInset && "px-4 sm:px-6",
									hasHeaderBorder && "border-b-background-medium border-b",
								)}
							>
								{title && (
									<DialogPrimitive.Title
										className={clsx(
											"flex-none text-lg font-bold leading-tight",
											titleClassName,
										)}
									>
										{title}
									</DialogPrimitive.Title>
								)}
								{subtitle}
							</header>
						)}
						<div className="relative z-10 h-0" role="presentation">
							<div
								className={clsx(
									"border-t-background-medium pointer-events-none absolute inset-x-0 top-0 h-3 border-t bg-gradient-to-b from-[color:hsla(0,0%,0%,0.025)]",
									isScrolledToTop ? "opacity-0" : "opacity-100",
								)}
							/>
						</div>
						<div
							ref={scrollContainerRef}
							onScroll={onScrollContainerScroll}
							className={clsx(
								"global-custom-scrollbar flex-1 overflow-auto",
								isScrollAreaInset && "-mx-1 px-1",
							)}
						>
							<div ref={scrollContainerContentRef} className="h-full">
								{children}
							</div>
						</div>
						<div className="relative z-10 h-0" role="presentation">
							<div
								className={clsx(
									"border-b-background-medium pointer-events-none absolute inset-x-0 bottom-0 h-3 border-b bg-gradient-to-t from-[color:hsla(0,0%,0%,0.025)]",
									isScrolledToBottom ? "opacity-0" : "opacity-100",
									isFooterFloating && "border-b-0",
								)}
							/>
						</div>
						{footerContent && (
							<footer
								className={clsx(
									"flex flex-none justify-end gap-3 pt-3 sm:pt-4",
									!isScrollAreaInset && "px-4 pb-4 sm:px-6 sm:pb-6",
									isFooterFloating &&
										"pointer-events-none absolute inset-x-0 bottom-0 bg-none [&_button]:pointer-events-auto",
									footerClassName,
								)}
							>
								{footerContent}
							</footer>
						)}
						{overlayContent && (
							<div className="absolute inset-0 z-20">{overlayContent}</div>
						)}
					</div>
				</div>
			</DialogPrimitive.Content>
		</DialogPrimitive.Portal>
	);
}

export const Dialog = DialogPrimitive.Root;

export const DialogTrigger = DialogPrimitive.Trigger;

export const DialogClose = DialogPrimitive.Close;
